import { useMemo, useState } from 'react';
import { LessonHeader } from './LessonHeader';
import { screenShell } from './common';
import { ErrorState, LoadingState } from '../../components/ScreenState';
import { SpeakButton } from '../../components/SpeakButton';
import { ttsTextOf } from '../../components/speech';
import { WEB_ICO } from '../../shell/icons';
import { useAsync } from '../../hooks/useAsync';
import { contentApi } from '../../api/content';
import type { LessonScreenProps } from './types';

export function VocabIntroScreen({ lessonId, unitName, lessonName, onBack, onNext }: LessonScreenProps) {
  const lesson = useAsync(() => contentApi.getLesson(lessonId), [lessonId]);
  const [index, setIndex] = useState(0);

  const items = useMemo(() => (lesson.data ? lesson.data.items.filter((it) => it.itemType === 'VOCAB') : []), [lesson.data]);
  const item = items[index];

  if (lesson.loading) return <LoadingState />;
  if (lesson.error || !lesson.data) return <ErrorState message="Không tải được bài học." />;
  if (!item || item.itemType !== 'VOCAB') return <ErrorState message="Bài học này chưa có nội dung từ vựng." />;

  const next = () => {
    if (index < items.length - 1) setIndex(index + 1);
    else onNext();
  };

  return (
    <div style={screenShell}>
      <LessonHeader onBack={onBack} ctx={{ track: 'vocab', unit: unitName, lesson: lessonName, step: 1, total: 2 }} />
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24 }}>
        {item.kanji && <div style={{ fontFamily: 'var(--font-jp)', fontSize: 15, color: 'var(--text-secondary)' }}>{item.kana}</div>}
        <div style={{ fontFamily: 'var(--font-jp)', fontSize: item.kanji ? 72 : 88, fontWeight: 400, lineHeight: 1.15, color: 'var(--ink)', textAlign: 'center' }}>
          {item.kanji ?? item.kana}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 8, width: '100%' }}>
          <span style={{ fontSize: 22, fontWeight: 600, color: 'var(--ink)', textAlign: 'center' }}>{item.meaningVi}</span>
          <SpeakButton text={ttsTextOf(item)} />
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 16 }}>{`${index + 1}/${items.length}`}</div>
      </div>
      <div style={{ display: 'flex', gap: 12, padding: 20, borderTop: '1px solid var(--border-default)' }}>
        <button
          onClick={() => index > 0 && setIndex(index - 1)}
          disabled={index === 0}
          aria-label="Thẻ trước"
          style={{
            width: 64,
            height: 52,
            borderRadius: 'var(--radius-control)',
            border: '1.5px solid var(--border-default)',
            background: 'var(--surface-page)',
            cursor: index === 0 ? 'not-allowed' : 'pointer',
            opacity: index === 0 ? 0.4 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {WEB_ICO.back('var(--ink)', 20)}
        </button>
        <button
          onClick={next}
          style={{
            flex: 1,
            height: 52,
            borderRadius: 'var(--radius-control)',
            background: 'var(--cta-default)',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'var(--font-vi)',
            fontWeight: 600,
            fontSize: 16,
          }}
        >
          Tiếp theo
        </button>
      </div>
    </div>
  );
}
